import { useEffect, useState } from 'react'

type CountdownTime = {
  hours: number
  minutes: number
  seconds: number
}

const COUNTDOWN_KEY = 'arsenal_countdown_end'
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000
const INITIAL_TIME: CountdownTime = { hours: 23, minutes: 59, seconds: 40 }

let endTime: number | null = null
let intervalId: ReturnType<typeof setInterval> | null = null
const subscribers = new Set<(time: CountdownTime) => void>()

export function useCountdown() {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>(INITIAL_TIME)

  useEffect(() => {
    subscribers.add(setTimeLeft)
    publishTime()
    startClock()

    return () => {
      subscribers.delete(setTimeLeft)
      if (subscribers.size === 0 && intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
    }
  }, [])

  return timeLeft
}

function startClock() {
  if (intervalId) return

  intervalId = setInterval(publishTime, 1000)
}

function publishTime() {
  const time = getTimeRemaining(getEndTime())
  subscribers.forEach((subscriber) => subscriber(time))

  if (
    time.hours === 0 &&
    time.minutes === 0 &&
    time.seconds === 0 &&
    intervalId
  ) {
    clearInterval(intervalId)
    intervalId = null
  }
}

function getEndTime() {
  if (endTime) return endTime

  const fallbackEndTime = Date.now() + DAY_IN_MILLISECONDS
  if (typeof window === 'undefined') {
    endTime = fallbackEndTime
    return endTime
  }

  try {
    const storedEndTime = Number(window.localStorage.getItem(COUNTDOWN_KEY))
    endTime = storedEndTime > Date.now() ? storedEndTime : fallbackEndTime
    window.localStorage.setItem(COUNTDOWN_KEY, endTime.toString())
  } catch {
    endTime = fallbackEndTime
  }

  return endTime
}

function getTimeRemaining(targetTime: number): CountdownTime {
  const difference = Math.max(0, targetTime - Date.now())

  return {
    hours: Math.floor((difference % DAY_IN_MILLISECONDS) / (60 * 60 * 1000)),
    minutes: Math.floor((difference % (60 * 60 * 1000)) / (60 * 1000)),
    seconds: Math.floor((difference % (60 * 1000)) / 1000),
  }
}
